import {

  TARGET_SAMPLE_RATE,
  cepstralPeakProminence,
  harmonicsToNoise,
  iqr,
  jitterLocal,
  median,
  resample,
  rms,
  shimmerLocal,
  voicedFrames,
  yinPitch,
} from './dsp';

/**
 * One recording's measured acoustic properties.
 *
 * Every field is a MEASUREMENT. Nothing here is an inference about health.
 */
export type VoiceFeatures = {
  /** Median fundamental frequency across voiced frames, Hz. */
  f0: number;
  /** Interquartile range of F0 — how steady the pitch was. */
  f0Iqr: number;
  /** Local jitter, percent. */
  jitter: number;
  /** Local shimmer, percent. */
  shimmer: number;
  /** Harmonics-to-noise ratio, dB. */
  hnr: number;
  /** Smoothed cepstral peak prominence, dB. */
  cpps: number;
  /** Fraction of the recording that was voiced, 0-1. */
  voicedRatio: number;
  /** Seconds of usable audio. */
  duration: number;
  /** How much to trust this sample, 0-1. */
  quality: number;
  /** Reasons quality is below 1. */
  warnings: string[];
};

const FRAME_MS = 40;
const HOP_MS = 20;

/**
 * Analyse one recording.
 *
 * Deliberately downsamples to 16 kHz first — see the note at the top of dsp.ts.
 * Higher sample rates give WORSE pitch resolution for a given FFT size.
 */
export function analyzeRecording(samples: Float32Array, sampleRate: number): VoiceFeatures {
  const warnings: string[] = [];
  const signal = resample(samples, sampleRate, TARGET_SAMPLE_RATE);
  const duration = signal.length / TARGET_SAMPLE_RATE;

  const frameSize = Math.round((FRAME_MS / 1000) * TARGET_SAMPLE_RATE);
  const hopSize = Math.round((HOP_MS / 1000) * TARGET_SAMPLE_RATE);
  const totalPossible = Math.max(1, Math.floor((signal.length - frameSize) / hopSize) + 1);

  const { frames } = voicedFrames(signal, frameSize, hopSize);
  const voicedRatio = frames.length / totalPossible;

  if (duration < 3) warnings.push('Recording is very short — aim for at least 5 seconds.');
  if (voicedRatio < 0.3) {
    warnings.push('Much of the recording was silent or too quiet to measure.');
  }

  const f0s: number[] = [];
  const periods: number[] = [];
  const amplitudes: number[] = [];
  const hnrs: number[] = [];

  for (const frame of frames) {
    const pitch = yinPitch(frame, TARGET_SAMPLE_RATE);
    // Only frames with a confident periodic estimate contribute.
    if (pitch.hz > 0 && pitch.probability > 0.5) {
      f0s.push(pitch.hz);
      periods.push(1 / pitch.hz);
      amplitudes.push(rms(frame));
      hnrs.push(harmonicsToNoise(frame, TARGET_SAMPLE_RATE, pitch.hz));
    }
  }

  if (f0s.length < 5) {
    warnings.push('Not enough steady voiced sound to measure reliably.');
    return {
      f0: 0,
      f0Iqr: 0,
      jitter: 0,
      shimmer: 0,
      hnr: 0,
      cpps: 0,
      voicedRatio,
      duration,
      quality: 0,
      warnings,
    };
  }

  // CPPS is expensive (naive DFT), so run it on a few representative frames
  // near the middle of the recording rather than all of them.
  const midStart = Math.max(0, Math.floor(frames.length / 2) - 3);
  const cppsFrames = frames.slice(midStart, midStart + 6);
  const cppsValues = cppsFrames.map((f) => cepstralPeakProminence(f, TARGET_SAMPLE_RATE));

  const f0 = median(f0s);
  const f0IqrValue = iqr(f0s);

  if (f0IqrValue > f0 * 0.25) {
    warnings.push('Pitch varied a lot — hold a steadier tone for a more repeatable result.');
  }

  // Quality is driven by how much usable material we got, not by whether the
  // values look "good". A confident measurement of an unusual voice is still
  // high quality.
  const quality = Math.max(
    0,
    Math.min(
      1,
      Math.min(1, duration / 8) * 0.4 +
        Math.min(1, voicedRatio / 0.6) * 0.3 +
        Math.min(1, f0s.length / 60) * 0.3,
    ),
  );

  return {
    f0,
    f0Iqr: f0IqrValue,
    jitter: jitterLocal(periods),
    shimmer: shimmerLocal(amplitudes),
    hnr: median(hnrs),
    cpps: median(cppsValues),
    voicedRatio,
    duration,
    quality,
    warnings,
  };
}

